import { useState, useEffect, useCallback } from 'react';
import { ProjectStatus, BudgetAllocation } from '../../types/projects';
import { projectsApi, ProjectWithBuilding } from '../../api/projectsApi';
import { ProjectPhotoType } from '../../Helpers/ProjectPhotoUtils';
import { workOrdersApi } from '../../api/workOrdersApi';
import { WorkOrderStatus } from '../../types/workOrders';

interface UseProjectProps {
    buildingKey: string;
    projectKey: string;
}

interface UseProjectReturn {
    project: ProjectWithBuilding | null;
    loading: boolean;
    error: string | null;
    submitting: boolean;

    // Core project actions
    updateTitle: (title: string) => Promise<void>;
    updateDescription: (description: string) => Promise<void>;
    updateStatus: (status: ProjectStatus, actualEndDate?: string) => Promise<void>;
    reopenProject: () => Promise<void>;
    updateStartDate: (startDate: string) => Promise<void>;
    updatePlannedEndDate: (plannedEndDate: string) => Promise<void>;
    updateActualEndDate: (actualEndDate: string) => Promise<void>;
    updateBudgetAllocation: (allocation: BudgetAllocation | null) => Promise<void>;
    deleteProject: () => Promise<void>;

    // Assignment
    assignProject: (userId: string | null) => Promise<void>;
    addProjectAssignee: (userId: string, sendEmail?: boolean) => Promise<void>;
    removeProjectAssignee: (userId: string) => Promise<void>;

    // Beställare (informational internal-user references)
    updateResponsibleOrderer: (userId: string | null) => Promise<void>;
    updateSecondaryOrderer: (userId: string | null) => Promise<void>;

    // Note actions
    addNote: (title: string, content: string) => Promise<void>;
    updateNote: (noteKey: string, title: string, content: string) => Promise<void>;
    deleteNote: (noteKey: string) => Promise<void>;

    // Checklist actions
    addChecklist: (title: string) => Promise<void>;
    updateChecklistTitle: (checklistKey: string, title: string) => Promise<void>;
    deleteChecklist: (checklistKey: string) => Promise<void>;
    addChecklistItem: (checklistKey: string, text: string) => Promise<void>;
    updateChecklistItemText: (checklistKey: string, itemKey: string, text: string) => Promise<void>;
    toggleChecklistItem: (checklistKey: string, itemKey: string, completed: boolean) => Promise<void>;
    deleteChecklistItem: (checklistKey: string, itemKey: string) => Promise<void>;
    reorderChecklistItems: (checklistKey: string, orderedItemKeys: string[]) => Promise<void>;

    // Photo actions
    uploadPhoto: (photoType: ProjectPhotoType, file: File) => Promise<boolean>;
    uploadPhotos: (photoType: ProjectPhotoType, files: File[]) => Promise<void>;
    deletePhoto: (photoType: ProjectPhotoType, photoKey: string) => Promise<void>;

    // Attachment actions
    uploadAttachment: (file: File, displayName?: string, opts?: { refresh?: boolean }) => Promise<boolean>;
    deleteAttachment: (attachmentKey: string) => Promise<void>;
    /** Throws on failure so the rename modal can show the error inline. */
    renameAttachment: (attachmentKey: string, displayName: string | undefined) => Promise<void>;

    // Contact list actions (internal + external)
    addExternalContact: (externalUserId: string, projectNote?: string, contactType?: 'internal' | 'external') => Promise<void>;
    removeExternalContact: (externalUserId: string) => Promise<void>;
    updateExternalContactNote: (externalUserId: string, note: string) => Promise<void>;

    // Utility
    clearError: () => void;
    refetch: () => Promise<void>;
}

export const useProject = ({ buildingKey, projectKey }: UseProjectProps): UseProjectReturn => {
    const [project, setProject] = useState<ProjectWithBuilding | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Load project (one-time fetch)
    const loadProject = useCallback(async () => {
        if (!buildingKey || !projectKey) {
            setProject(null);
            setLoading(false);
            return;
        }

        try {
            const projectData = await projectsApi.getProject(buildingKey, projectKey);
            setProject(projectData);
        } catch (err) {
            console.error('Error loading project:', err);
            setError('Kunde inte ladda projektet');
        }
    }, [buildingKey, projectKey]);

    // Load on mount and when keys change
    useEffect(() => {
        setLoading(true);
        setError(null);
        loadProject().finally(() => setLoading(false));
    }, [loadProject]);

    const refetch = useCallback(async () => {
        await loadProject();
    }, [loadProject]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Helper to wrap actions with loading state and refetch
    const withSubmitting = useCallback(async <T,>(
        action: () => Promise<T>,
        errorMessage: string,
        shouldRefetch = true
    ): Promise<T | undefined> => {
        if (!buildingKey || !projectKey) return undefined;

        setSubmitting(true);
        setError(null);

        try {
            const result = await action();
            if (shouldRefetch) {
                await loadProject();
            }
            return result;
        } catch (err) {
            console.error(errorMessage, err);
            setError(err instanceof Error ? err.message : errorMessage);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [buildingKey, projectKey, loadProject]);

    // ============ CORE PROJECT ACTIONS ============

    const updateTitle = useCallback(async (title: string) => {
        await withSubmitting(
            () => projectsApi.updateTitle(buildingKey, projectKey, title),
            'Kunde inte uppdatera titel'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const updateDescription = useCallback(async (description: string) => {
        await withSubmitting(
            () => projectsApi.updateDescription(buildingKey, projectKey, description),
            'Kunde inte uppdatera beskrivning'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const updateStatus = useCallback(async (status: ProjectStatus, actualEndDate?: string) => {
        await withSubmitting(
            async () => {
                // Validate completion - check for open work orders
                if (status === ProjectStatus.COMPLETED) {
                    const buildingWOs = await workOrdersApi.getByBuilding(buildingKey);
                    const openProjectWOs = buildingWOs.filter(wo =>
                        wo.projectKey === projectKey && wo.status === WorkOrderStatus.OPEN
                    );

                    if (openProjectWOs.length > 0) {
                        throw new Error(
                            `Kan inte markera som utförd - det finns ${openProjectWOs.length} öppna ärenden`
                        );
                    }
                }

                return projectsApi.updateStatus(buildingKey, projectKey, status, actualEndDate);
            },
            'Kunde inte uppdatera status'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const reopenProject = useCallback(async () => {
        await withSubmitting(
            () => projectsApi.reopenProject(buildingKey, projectKey),
            'Kunde inte återöppna projektet'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const updateStartDate = useCallback(async (startDate: string) => {
        await withSubmitting(
            () => projectsApi.updateStartDate(buildingKey, projectKey, startDate),
            'Kunde inte uppdatera startdatum'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const updatePlannedEndDate = useCallback(async (plannedEndDate: string) => {
        await withSubmitting(
            () => projectsApi.updatePlannedEndDate(buildingKey, projectKey, plannedEndDate),
            'Kunde inte uppdatera planerat slutdatum'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const updateActualEndDate = useCallback(async (actualEndDate: string) => {
        await withSubmitting(
            () => projectsApi.updateActualEndDate(buildingKey, projectKey, actualEndDate),
            'Kunde inte uppdatera slutdatum'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const updateBudgetAllocation = useCallback(async (allocation: BudgetAllocation | null) => {
        await withSubmitting(
            () => projectsApi.updateBudgetAllocation(buildingKey, projectKey, allocation),
            'Kunde inte uppdatera budget'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const deleteProject = useCallback(async () => {
        await withSubmitting(
            async () => {
                // Check for open work orders before allowing deletion
                const buildingWOs = await workOrdersApi.getByBuilding(buildingKey);
                const openProjectWOs = buildingWOs.filter(wo =>
                    wo.projectKey === projectKey && wo.status === WorkOrderStatus.OPEN
                );

                if (openProjectWOs.length > 0) {
                    throw new Error(
                        `Kan inte radera - projektet har ${openProjectWOs.length} öppna ärenden`
                    );
                }

                return projectsApi.deleteProject(buildingKey, projectKey);
            },
            'Kunde inte radera projektet',
            false // Don't refetch after delete
        );
    }, [buildingKey, projectKey, withSubmitting]);

    // ============ ASSIGNMENT ACTIONS ============

    // Single-select compat (replaces the internal assignees with one, or clears).
    const assignProject = useCallback(async (userId: string | null) => {
        await withSubmitting(
            () => projectsApi.assign(projectKey, userId),
            'Kunde inte tilldela projektet'
        );
    }, [projectKey, withSubmitting]);

    // Multi-assignee: add/remove a single internal assignee without disturbing
    // the others or any external delegate.
    const addProjectAssignee = useCallback(async (userId: string, sendEmail: boolean = true) => {
        await withSubmitting(
            () => projectsApi.addAssignee(projectKey, userId, sendEmail),
            'Kunde inte lägga till tilldelad'
        );
    }, [projectKey, withSubmitting]);

    const removeProjectAssignee = useCallback(async (userId: string) => {
        await withSubmitting(
            () => projectsApi.removeAssignee(projectKey, userId),
            'Kunde inte ta bort tilldelad'
        );
    }, [projectKey, withSubmitting]);

    // ============ BESTÄLLARE ACTIONS ============

    const updateResponsibleOrderer = useCallback(async (userId: string | null) => {
        await withSubmitting(
            () => projectsApi.updateResponsibleOrderer(buildingKey, projectKey, userId),
            'Kunde inte uppdatera ansvarig beställare'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const updateSecondaryOrderer = useCallback(async (userId: string | null) => {
        await withSubmitting(
            () => projectsApi.updateSecondaryOrderer(buildingKey, projectKey, userId),
            'Kunde inte uppdatera sekundär beställare'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    // ============ NOTE ACTIONS ============

    const addNote = useCallback(async (title: string, content: string) => {
        await withSubmitting(
            () => projectsApi.addNote(buildingKey, projectKey, title, content),
            'Kunde inte lägga till anteckning'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const updateNote = useCallback(async (noteKey: string, title: string, content: string) => {
        await withSubmitting(
            () => projectsApi.updateNote(buildingKey, projectKey, noteKey, title, content),
            'Kunde inte uppdatera anteckning'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const deleteNote = useCallback(async (noteKey: string) => {
        await withSubmitting(
            () => projectsApi.deleteNote(buildingKey, projectKey, noteKey),
            'Kunde inte radera anteckning'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    // ============ CHECKLIST ACTIONS ============

    const addChecklist = useCallback(async (title: string) => {
        await withSubmitting(
            () => projectsApi.addChecklist(buildingKey, projectKey, title),
            'Kunde inte lägga till checklista'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const updateChecklistTitle = useCallback(async (checklistKey: string, title: string) => {
        await withSubmitting(
            () => projectsApi.updateChecklistTitle(buildingKey, projectKey, checklistKey, title),
            'Kunde inte uppdatera checklistatitel'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const deleteChecklist = useCallback(async (checklistKey: string) => {
        await withSubmitting(
            () => projectsApi.deleteChecklist(buildingKey, projectKey, checklistKey),
            'Kunde inte radera checklista'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const addChecklistItem = useCallback(async (checklistKey: string, text: string) => {
        await withSubmitting(
            () => projectsApi.addChecklistItem(buildingKey, projectKey, checklistKey, text),
            'Kunde inte lägga till checklistpunkt'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const updateChecklistItemText = useCallback(async (checklistKey: string, itemKey: string, text: string) => {
        await withSubmitting(
            () => projectsApi.updateChecklistItemText(buildingKey, projectKey, checklistKey, itemKey, text),
            'Kunde inte uppdatera checklistpunkt'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const toggleChecklistItem = useCallback(async (checklistKey: string, itemKey: string, completed: boolean) => {
        await withSubmitting(
            () => projectsApi.toggleChecklistItem(buildingKey, projectKey, checklistKey, itemKey, completed),
            'Kunde inte uppdatera checklistpunkt'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const deleteChecklistItem = useCallback(async (checklistKey: string, itemKey: string) => {
        await withSubmitting(
            () => projectsApi.deleteChecklistItem(buildingKey, projectKey, checklistKey, itemKey),
            'Kunde inte radera checklistpunkt'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const reorderChecklistItems = useCallback(async (checklistKey: string, orderedItemKeys: string[]) => {
        // Optimistically update local state with new sortOrder values
        setProject(prev => {
            if (!prev || !prev.checklists?.[checklistKey]?.items) return prev;

            const updatedItems = { ...prev.checklists[checklistKey].items };
            orderedItemKeys.forEach((itemKey, index) => {
                if (updatedItems[itemKey]) {
                    updatedItems[itemKey] = { ...updatedItems[itemKey], sortOrder: index };
                }
            });

            return {
                ...prev,
                checklists: {
                    ...prev.checklists,
                    [checklistKey]: {
                        ...prev.checklists[checklistKey],
                        items: updatedItems
                    }
                }
            };
        });

        // Persist to Firebase without refetching
        await withSubmitting(
            () => projectsApi.reorderChecklistItems(buildingKey, projectKey, checklistKey, orderedItemKeys),
            'Kunde inte ändra ordning',
            false
        );
    }, [buildingKey, projectKey, withSubmitting]);

    // ============ PHOTO ACTIONS ============

    const uploadPhoto = useCallback(async (photoType: ProjectPhotoType, file: File): Promise<boolean> => {
        if (!buildingKey || !projectKey) return false;

        setError(null);

        try {
            const result = await projectsApi.uploadPhoto(buildingKey, projectKey, photoType, file);
            if (!result.success) {
                setError(result.error || 'Kunde inte ladda upp foto');
                return false;
            }
            await loadProject();
            return true;
        } catch (err) {
            console.error('Error uploading photo:', err);
            setError(err instanceof Error ? err.message : 'Kunde inte ladda upp foto');
            return false;
        }
    }, [buildingKey, projectKey, loadProject]);

    const uploadPhotos = useCallback(async (photoType: ProjectPhotoType, files: File[]) => {
        if (!buildingKey || !projectKey || files.length === 0) return;

        setSubmitting(true);
        setError(null);

        try {
            // Upload all photos in parallel
            const results = await Promise.allSettled(
                files.map(file => projectsApi.uploadPhoto(buildingKey, projectKey, photoType, file))
            );

            // Check for failures
            const failures = results.filter(
                r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success)
            );

            if (failures.length > 0) {
                if (failures.length === files.length) {
                    setError('Kunde inte ladda upp foton');
                } else {
                    setError(`${failures.length} av ${files.length} foton kunde inte laddas upp`);
                }
            }

            // Single refetch after all uploads complete
            await loadProject();
        } catch (err) {
            console.error('Error uploading photos:', err);
            setError(err instanceof Error ? err.message : 'Kunde inte ladda upp foton');
        } finally {
            setSubmitting(false);
        }
    }, [buildingKey, projectKey, loadProject]);

    const deletePhoto = useCallback(async (photoType: ProjectPhotoType, photoKey: string) => {
        await withSubmitting(
            () => projectsApi.deletePhoto(buildingKey, projectKey, photoType, photoKey),
            'Kunde inte radera foto'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    // ============ ATTACHMENT ACTIONS ============

    // Batch flows (multi-file naming queue) pass refresh: false and refetch
    // once when the whole batch is done, instead of after every file
    const uploadAttachment = useCallback(async (file: File, displayName?: string, opts?: { refresh?: boolean }): Promise<boolean> => {
        if (!buildingKey || !projectKey) return false;

        setError(null);

        try {
            const result = await projectsApi.uploadAttachment(buildingKey, projectKey, file, displayName);
            if (!result.success) {
                setError(result.error || 'Kunde inte ladda upp bilaga');
                return false;
            }
            if (opts?.refresh !== false) {
                await loadProject();
            }
            return true;
        } catch (err) {
            console.error('Error uploading attachment:', err);
            setError(err instanceof Error ? err.message : 'Kunde inte ladda upp bilaga');
            return false;
        }
    }, [buildingKey, projectKey, loadProject]);

    const deleteAttachment = useCallback(async (attachmentKey: string) => {
        if (!buildingKey || !projectKey) return;

        setError(null);

        try {
            const result = await projectsApi.deleteAttachment(buildingKey, projectKey, attachmentKey);
            if (!result.success) {
                setError(result.error || 'Kunde inte radera bilaga');
                return;
            }
            await loadProject();
        } catch (err) {
            console.error('Error deleting attachment:', err);
            setError(err instanceof Error ? err.message : 'Kunde inte radera bilaga');
        }
    }, [buildingKey, projectKey, loadProject]);

    const renameAttachment = useCallback(async (attachmentKey: string, displayName: string | undefined) => {
        if (!projectKey) return;
        await projectsApi.renameAttachment(projectKey, attachmentKey, displayName);
        await loadProject();
    }, [projectKey, loadProject]);

    // ============ EXTERNAL CONTACT ACTIONS ============

    const addExternalContact = useCallback(async (externalUserId: string, projectNote?: string, contactType: 'internal' | 'external' = 'external') => {
        await withSubmitting(
            () => projectsApi.addExternalContact(buildingKey, projectKey, externalUserId, projectNote, contactType),
            'Kunde inte lägga till kontakt'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const removeExternalContact = useCallback(async (externalUserId: string) => {
        await withSubmitting(
            () => projectsApi.removeExternalContact(buildingKey, projectKey, externalUserId),
            'Kunde inte ta bort kontakt'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    const updateExternalContactNote = useCallback(async (externalUserId: string, note: string) => {
        await withSubmitting(
            () => projectsApi.updateExternalContactNote(buildingKey, projectKey, externalUserId, note),
            'Kunde inte uppdatera anteckning'
        );
    }, [buildingKey, projectKey, withSubmitting]);

    return {
        project,
        loading,
        error,
        submitting,

        updateTitle,
        updateDescription,
        updateStatus,
        reopenProject,
        updateStartDate,
        updatePlannedEndDate,
        updateActualEndDate,
        updateBudgetAllocation,
        deleteProject,

        assignProject,
        addProjectAssignee,
        removeProjectAssignee,

        updateResponsibleOrderer,
        updateSecondaryOrderer,

        addNote,
        updateNote,
        deleteNote,

        addChecklist,
        updateChecklistTitle,
        deleteChecklist,
        addChecklistItem,
        updateChecklistItemText,
        toggleChecklistItem,
        deleteChecklistItem,
        reorderChecklistItems,

        uploadPhoto,
        uploadPhotos,
        deletePhoto,

        uploadAttachment,
        deleteAttachment,
        renameAttachment,

        addExternalContact,
        removeExternalContact,
        updateExternalContactNote,

        clearError,
        refetch,
    };
};
